import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { feedbackAPI } from '../services/api';
import myshieldLogo from '../assets/myshield-logo.png';
import './PortalWizard.css';

// ─── Wizard step data ────────────────────────────────────────────────────────

const SVG = { viewBox: '0 0 24 24', fill: 'none', stroke: '#002060', strokeWidth: '1.5', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, width: 44, height: 44 };

const IconDashboard    = <svg {...SVG}><rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="3" width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/></svg>;
const IconVehicle      = <svg {...SVG}><rect x="1" y="8" width="15" height="11" rx="1"/><path d="M16 11l4 3v5h-4V11z"/><circle cx="5.5" cy="19.5" r="2.5"/><circle cx="18.5" cy="19.5" r="2.5"/></svg>;
const IconShield       = <svg {...SVG}><path d="M12 2L4 6v5c0 5 3.5 9.74 8 11 4.5-1.26 8-6 8-11V6L12 2z"/><polyline points="9 12 11 14 15 10"/></svg>;
const IconDriver       = <svg {...SVG}><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/></svg>;
const IconUpload       = <svg {...SVG}><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></svg>;
const IconClipboard    = <svg {...SVG}><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><polyline points="9 12 11 14 15 10"/></svg>;
const IconEnvelope     = <svg {...SVG}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/></svg>;
const IconChart        = <svg {...SVG}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>;
const IconComplete     = <svg {...SVG}><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>;

interface Step {
  icon: React.ReactNode;
  title: string | ((isAdmin: boolean) => string);
  body: (isAdmin: boolean) => string;
  adminOnly?: boolean;
}

const STEPS: Step[] = [
  {
    icon: '__logo__',
    title: () => 'Welcome to Myshield',
    body: (a) =>
      a
        ? `Myshield is your central hub for managing client fleet insurance portfolios. This 10-step tour covers every key feature of the platform.\n\nClick Next to begin — you can skip at any time and re-open this guide from the ? button in the bottom-right corner.`
        : `Myshield is your platform for managing your company's fleet insurance cover. This 10-step tour will walk you through every key feature.\n\nClick Next to start — you can skip and re-open this guide any time via the ? button in the bottom-right corner.`,
  },
  {
    icon: IconDashboard,
    title: 'Your Dashboard',
    body: (a) =>
      a
        ? `The Dashboard is your command centre — live vehicle counts, cover status breakdown across all clients, recent workstream activity, and any actions requiring your attention. It updates in real time as events occur.`
        : `The Dashboard gives you an instant overview of your fleet: total vehicles, cover status breakdown, recent changes and any pending requests. Everything you need is just one click away from here.`,
  },
  {
    icon: IconVehicle,
    title: 'Vehicle Management',
    body: (a) =>
      a
        ? `The Vehicles section shows every vehicle across all clients. Filter by client, cover status or search by registration. View the full history of any vehicle, update cover status directly, manage modifications, and export data for insurer submissions.`
        : `Submit new vehicles for cover from the Vehicles page. Your Myshield handler reviews each submission before confirming cover. You can view current cover status, request changes, and see the full history of each vehicle on the policy.`,
  },
  {
    icon: IconShield,
    title: 'Understanding Cover Status',
    body: (a) =>
      a
        ? `Vehicles move through three cover statuses:\n\n• Pending Cover — submitted, awaiting your review and approval\n• On Cover — active insurance cover confirmed\n• Awaiting Inception — approved with a future cover start date; cover is confirmed but has not yet commenced. The vehicle will move to On Cover automatically on the cover start date.\n\nReview and approve all submissions via the Workstream. If Instant Cover is enabled for a client, vehicles skip Pending Cover and go straight to On Cover (or Awaiting Inception if a future start date is set).`
        : `Vehicles move through three cover statuses:\n\n• Pending Cover — submitted and awaiting approval by your Myshield handler\n• On Cover — active insurance cover has been confirmed\n• Awaiting Inception — your vehicle has been approved but the cover start date is set in the future. It will automatically move to On Cover when that date arrives. Look for the pulsing purple label in your Vehicles list.\n\nYou will receive a notification in Messages when the status of any vehicle changes.`,
  },
  {
    icon: IconDriver,
    title: 'Referred Named Drivers',
    body: (a) =>
      a
        ? `The Drivers section lists all named and approved drivers for each client. Review uploaded licence images for compliance, manage each client's Named Driver Policy, and monitor driver consent records — all maintained in a full audit trail.`
        : `Add and monitor all drivers who fall outside of your insurers driver warranty who have been referred to the insurer for acceptance. If you are unsure on your policies driver warranty terms, please contact your Myshield handler.`,
  },
  {
    icon: IconUpload,
    title: 'Bulk CSV Upload',
    body: () =>
      `Onboard large fleets quickly using bulk CSV upload. Download the template from the Vehicles page, populate it with vehicle data, and upload in one action. If a client has Instant Cover enabled, all uploaded vehicles go straight to On Cover. Batches can be undone from Admin Tools.`,
    adminOnly: true,
  },
  {
    icon: IconClipboard,
    title: (a) => (a ? 'Workstream — Approvals Queue' : 'Cover Requests'),
    body: (a) =>
      a
        ? `The Workstream is your approval queue for all client requests — new vehicle additions, reinstatements, removals, temporary cover and change-of-vehicle requests. Approve individually with notes and cover dates, or bulk-approve multiple items in a single action.`
        : `From the Vehicles page you can submit requests to: add a new vehicle, remove a vehicle, request temporary cover for a date range, or change a vehicle registration. Your Myshield handler reviews each request and responds with a decision and any relevant notes.`,
  },
  {
    icon: IconEnvelope,
    title: 'Messages & Cover Notices',
    body: (a) =>
      a
        ? `Messages houses all cover notices and insurer communications. Send mid-term adjustment notes, communicate approvals and declines to clients, and maintain a complete audit trail of all policy correspondence — all in one place.`
        : `The Messages section keeps you informed about your fleet cover. Receive notifications when cover is approved or declined, when policy updates are issued, and when your Myshield handler sends important information about your account.`,
  },
  {
    icon: IconChart,
    title: 'Insights & Reporting',
    body: (a) =>
      a
        ? `Generate fully customisable reports from Insights & Reporting. Filter by client, date range and event type. Export to CSV or PDF for insurer mid-term adjustment submissions, portfolio reviews and account management reporting.`
        : `The Insights page gives you a clear picture of your fleet activity — vehicle additions, removals, cover changes and a full event timeline. Export reports for your own records, finance teams or insurance renewal conversations.`,
  },
  {
    icon: IconComplete,
    title: "You're All Set!",
    body: (a) =>
        a
          ? `That covers all the key areas of Myshield. The ? help button in the bottom-right is always available — browse FAQs for step-by-step guidance, restart this tour any time, or submit feedback to help us improve the platform.\n\nWelcome to Myshield!`
          : `That covers all the key areas of Myshield. The ? help button in the bottom-right is always available — browse FAQs, restart this tour any time, or send feedback if you spot something we can improve.\n\nYour Myshield team is always here to help.`,
  },
];

// ─── FAQ data ────────────────────────────────────────────────────────────────

interface FaqItem {
  q: string;
  a: string;
  role?: 'admin' | 'client';
}

const FAQS: FaqItem[] = [
  // ── Client only ──────────────────────────────────────────────────────────────
  {
    q: 'How do I add a vehicle?',
    a: "Go to Vehicles \u2192 Add Vehicle. Enter the registration and use the DVLA lookup to auto-fill the details, or enter them manually. Submit the form \u2014 your Myshield handler will review and confirm cover.",
    role: 'client',
  },
  {
    q: "What does 'Pending Cover' mean?",
    a: "Pending Cover means your vehicle has been submitted but is awaiting review and approval by your Myshield handler. The vehicle is not yet insured under the policy. You will be notified in Messages as soon as the status changes to On Cover.",
    role: 'client',
  },
  {
    q: "What does 'Awaiting Inception' mean?",
    a: "Awaiting Inception means your vehicle has been approved and cover is confirmed, but the cover start date is set in the future. The vehicle will automatically move to On Cover when that date arrives. You will see it shown with a pulsing purple label in your Vehicles list.",
    role: 'client',
  },
  {
    q: "What does 'Awaiting Inception' mean in the workstream?",
    a: "Awaiting Inception is a vehicle that has been approved with a future cover start date. It is not yet On Cover but is committed to become so on the set date. It appears with a pulsing purple label in the vehicle list.",
    role: 'admin',
  },
  {
    q: 'How do I request temporary cover?',
    a: "On the Vehicles page, open the actions menu on the vehicle and select Request Temporary Cover. Enter the start and end dates, add any notes, and submit. Your Myshield handler will review and confirm.",
    role: 'client',
  },
  {
    q: 'How do I remove a vehicle from cover?',
    a: "On the Vehicles page, open the actions menu on the vehicle and select Request Removal. Provide the requested cover end date and any relevant notes. Your Myshield handler will process the removal and confirm.",
    role: 'client',
  },
  {
    q: 'How do I add a named driver?',
    a: "Go to Named Drivers → Add Driver. Fill in their name, date of birth, licence number, licence type and category. After saving, click the edit (pencil) icon to upload the front and back licence images.",
    role: 'client',
  },
  {
    q: 'How do I manage my sites and depots?',
    a: "Use Sites in the sidebar to view and manage your fleet's depots and locations. Click Add Site to register a new location. Sites can be linked to individual vehicles for better fleet organisation.",
    role: 'client',
  },
  {
    q: 'How do I view my consent history?',
    a: "Go to Consent Records in the sidebar. You can view your own consent history here — including when you accepted the data privacy notice and any driver licence consent events.",
    role: 'client',
  },
  // ── Admin only ──────────────────────────────────────────────────────────────
  {
    q: 'How do I approve a vehicle for cover?',
    a: "Open Workstream from the sidebar. Find the pending submission, review the vehicle details, set a cover effective date, add approval notes if needed, and click Approve. You can also bulk-approve multiple vehicles at once.",
    role: 'admin',
  },
  {
    q: 'How do I enable Instant Cover for a client?',
    a: "Open the Client Record from the Clients page and click the Cover Settings tab. Toggle Instant Cover on and click Save. New vehicle submissions for that client will automatically go straight to On Cover without manual approval.",
    role: 'admin',
  },
  {
    q: 'How do I create a client user account?',
    a: "Open the Client Record from the Clients page, click the Users tab, then Add User. Enter their name, email, username and a temporary password and click Create User. They can log in immediately.",
    role: 'admin',
  },
  {
    q: 'How do I undo a bulk upload?',
    a: "Go to Admin Tools and find the Undo Bulk Upload section. Enter the batch ID shown in the upload confirmation, then click Undo. This removes all vehicles from that specific upload batch.",
    role: 'admin',
  },
  {
    q: 'How do I manage client sites?',
    a: "Open the Client Record from the Clients page and click the Sites tab. Add, edit or remove depot and location records here. Sites can be linked to vehicles and are useful for multi-site fleet management.",
    role: 'admin',
  },
  {
    q: 'How do I view consent records?',
    a: "Go to Consent Records in the sidebar. Filter by client, consent type or search term to find specific events. Records are logged automatically for data privacy acceptances, driver licence consent and other Environment events.",
    role: 'admin',
  },
  // ── Shared (shown to both) ───────────────────────────────────────────────────
  {
    q: 'How do I bulk upload vehicles?',
    a: "On the Vehicles page, click Bulk Upload via CSV. Download the template, fill in the vehicle data (Registration, Make, Model, Type, Weight, Year, Value, optional Site Name), then upload the completed file. Row-level errors are shown with the reason for each issue.",
  },
  {
    q: "How do I upload a driver's licence image?",
    a: "On the Named Drivers page, find the driver and click the edit (pencil) icon. Scroll to the Licence Images section at the bottom of the form and upload the front and back images — PNG and JPEG are supported.",
  },
  {
    q: 'How do I generate an Insights report?',
    a: "Go to Insights & Reporting from the sidebar. Set a date range and choose which event types to include. Click Generate Report to view the results, then use the Export button to download as CSV or PDF.",
  },
  {
    q: 'How do I restart this tour?',
    a: "Click the ? button in the bottom-right corner of any page. In the Help panel, go to the Guide tab and click Start Tour. The tour will restart from the beginning.",
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

const PortalWizard: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const location = useLocation();

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Panel state
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelTab, setPanelTab] = useState<'tour' | 'faq' | 'feedback'>('faq');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  // Feedback state
  const [fbCategory, setFbCategory] = useState('Feature Request');
  const [fbMessage, setFbMessage] = useState('');
  const [fbEmail, setFbEmail] = useState('');
  const [fbEmailError, setFbEmailError] = useState(false);
  const [fbTicketId, setFbTicketId] = useState<number | null>(null);
  const [fbSubmitting, setFbSubmitting] = useState(false);
  const [fbDone, setFbDone] = useState(false);
  const [fbError, setFbError] = useState(false);

  // Fire wizard after GDPR acceptance — Login.tsx sets fleetWizardPending then navigates here.
  // Guard on gdprConsent so a stale fleetWizardPending from a previous session (e.g. logged
  // out mid-wizard) cannot bypass the GDPR declaration on the next login.
  // NOTE: fleetWizardPending is NOT removed here; it is removed when the wizard is closed or
  // finished so that the Academy modal polling can detect completion.
  useEffect(() => {
    const gdprAccepted = sessionStorage.getItem('gdprConsent') === 'accepted';
    if (gdprAccepted && sessionStorage.getItem('fleetWizardPending') === '1') {
      const t = setTimeout(() => {
        setStep(0);
        setWizardOpen(true);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [location.pathname]);

  const closeWizard = () => {
    sessionStorage.removeItem('fleetWizardPending');
    if (user?.id) localStorage.setItem(`tourDone-${user.id}`, '1');
    setWizardOpen(false);
  };
  const finishWizard = () => {
    sessionStorage.removeItem('fleetWizardPending');
    if (user?.id) localStorage.setItem(`tourDone-${user.id}`, '1');
    setWizardOpen(false);
  };

  const restartTour = () => {
    setPanelOpen(false);
    setStep(0);
    setTimeout(() => setWizardOpen(true), 200);
  };

  const handleFabClick = () => setPanelOpen((prev) => !prev);

  const handleSubmitFeedback = async () => {
    if (!fbMessage.trim()) return;
    if (!fbEmail.trim()) { setFbEmailError(true); return; }
    setFbEmailError(false);
    setFbSubmitting(true);
    setFbError(false);
    try {
      const res = await feedbackAPI.submit({ category: fbCategory, message: fbMessage, email: fbEmail.trim() });
      setFbTicketId(res.data?.id ?? null);
      setFbDone(true);
      setFbMessage('');
      setFbEmail('');
      setTimeout(() => { setFbDone(false); setFbTicketId(null); }, 8000);
    } catch {
      setFbError(true);
    } finally {
      setFbSubmitting(false);
    }
  };

  // FAQs filtered to current user role (role-unspecified items shown to everyone)
  const filteredFaqs = FAQS.filter(
    (f) => !f.role || f.role === (isAdmin ? 'admin' : 'client')
  );

  // Filter steps based on role (admin-only steps hidden from clients)
  const visibleSteps = STEPS.filter(s => !s.adminOnly || isAdmin);

  // Current step helpers
  const currentStep = visibleSteps[step] ?? visibleSteps[0];
  const stepTitle =
    typeof currentStep.title === 'function'
      ? currentStep.title(isAdmin)
      : currentStep.title;
  const stepBody = currentStep.body(isAdmin);
  const progressPct = ((step + 1) / visibleSteps.length) * 100;
  const isLastStep = step === visibleSteps.length - 1;

  return (
    <>
      {/* ── Wizard Overlay ──────────────────────────────────────────────── */}
      {wizardOpen && (
        <div className="pw-overlay" onClick={closeWizard}>
          <div className="pw-card" onClick={(e) => e.stopPropagation()}>
            {/* Progress bar */}
            <div className="pw-progress-bar">
              <div className="pw-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>

            {/* Card header */}
            <div className="pw-card-header">
              <span className="pw-step-counter">
                Step {step + 1} of {visibleSteps.length}
              </span>
              <button className="pw-card-skip" onClick={closeWizard}>
                Skip tour ✕
              </button>
            </div>

            {/* Card body */}
            <div className="pw-card-body">
              <div key={step} className="pw-step-icon">
                {step === 0
          ? <img src={myshieldLogo} alt="Myshield" className="pw-step-logo" />
                  : currentStep.icon}
              </div>
              <h2 className="pw-step-title">{stepTitle}</h2>
              <p className="pw-step-content">{stepBody}</p>
            </div>

            {/* Step dots */}
            <div className="pw-step-dots">
              {visibleSteps.map((_, i) => (
                <button
                  key={i}
                  className={`pw-dot ${i === step ? 'active' : i < step ? 'done' : ''}`}
                  onClick={() => setStep(i)}
                  aria-label={`Go to step ${i + 1}`}
                />
              ))}
            </div>

            {/* Card footer */}
            <div className="pw-card-footer">
              <button
                className="pw-btn pw-btn-ghost"
                onClick={() => setStep((s) => s - 1)}
                disabled={step === 0}
              >
                ← Back
              </button>
              <div className="pw-nav-group">
                {isLastStep ? (
                  <button className="pw-btn pw-btn-finish" onClick={finishWizard}>
                    ✓ Finish Tour
                  </button>
                ) : (
                  <button
                    className="pw-btn pw-btn-primary"
                    onClick={() => setStep((s) => s + 1)}
                  >
                    Next →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Help Panel ──────────────────────────────────────────────────── */}
      {panelOpen && (
        <div className="pw-panel">
          {/* Header */}
          <div className="pw-panel-header">
            <span className="pw-panel-header-icon">🧭</span>
          <div style={{ flex: 1 }}>
              <h3>Myshield Help</h3>
              <p>Guides, FAQs &amp; Feedback</p>
            </div>
            <button className="pw-panel-close" onClick={() => setPanelOpen(false)}>
              ✕
            </button>
          </div>

          {/* Tab bar */}
          <div className="pw-tabs">
            <button
              className={`pw-tab ${panelTab === 'tour' ? 'active' : ''}`}
              onClick={() => setPanelTab('tour')}
            >
              <span className="pw-tab-icon">🗺️</span>
              Guide
            </button>
            <button
              className={`pw-tab ${panelTab === 'faq' ? 'active' : ''}`}
              onClick={() => setPanelTab('faq')}
            >
              <span className="pw-tab-icon">❓</span>
              FAQ
            </button>
            <button
              className={`pw-tab ${panelTab === 'feedback' ? 'active' : ''}`}
              onClick={() => setPanelTab('feedback')}
            >
              <span className="pw-tab-icon">💬</span>
              Feedback
            </button>
          </div>

          {/* Panel body */}
          <div className="pw-panel-body">
            {/* ── Tour tab ── */}
            {panelTab === 'tour' && (
              <div className="pw-tour-tab">
                <div className="pw-tour-tab-icon">🧭</div>
                <h4>Environment Tour</h4>
                <p>
                  Get a guided walkthrough of all 10 key features of Yourshield Myshield.
                  Each step explains what you can do and where to find it.
                </p>
                <p>
                  Step through at your own pace, jump to any section using the dots,
                  and close it at any time — the ? button is always here when you need a refresher.
                </p>
                <button className="pw-start-tour-btn" onClick={restartTour}>
                  🗺️ &nbsp;Start Tour
                </button>
              </div>
            )}

            {/* ── FAQ tab ── */}
            {panelTab === 'faq' && (
              <div className="pw-faq-list">
                {filteredFaqs.map((item, idx) => (
                  <div key={idx} className="pw-faq-item">
                    <button
                      className={`pw-faq-question ${activeFaq === idx ? 'open' : ''}`}
                      onClick={() =>
                        setActiveFaq(activeFaq === idx ? null : idx)
                      }
                    >
                      <span>{item.q}</span>
                      <span className="pw-faq-chevron">▼</span>
                    </button>
                    {activeFaq === idx && (
                      <div className="pw-faq-answer">{item.a}</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ── Feedback tab ── */}
            {panelTab === 'feedback' && (
              <div className="pw-feedback-tab">
                <h4>Share Your Feedback</h4>
                <p>
                  Found a bug? Have a suggestion? We read every submission and
                  use your feedback to improve Yourshield Myshield.
                </p>

                <label className="pw-feedback-label">Category</label>
                <select
                  className="pw-feedback-select"
                  value={fbCategory}
                  onChange={(e) => setFbCategory(e.target.value)}
                >
                  <option>Feature Request</option>
                  <option>Bug Report</option>
                  <option>General</option>
                  <option>Critical / System Issue</option>
                </select>
                {fbCategory === 'Critical / System Issue' && (
                  <div style={{ marginTop: 8, padding: '10px 12px', background: '#fef2f2', border: '1.5px solid #dc2626', borderRadius: 6, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                    🚨 For immediate system outages call <strong>0333 323 9660</strong> — do not rely solely on this form.
                  </div>
                )}

                <label className="pw-feedback-label">Your email address <span style={{ fontWeight: 600, color: '#dc2626' }}>*</span></label>
                <input
                  type="email"
                  className="pw-feedback-select"
                  placeholder="e.g. you@yourcompany.com"
                  value={fbEmail}
                  onChange={(e) => { setFbEmail(e.target.value); setFbEmailError(false); }}
                  style={{ marginBottom: fbEmailError ? 4 : 12, borderColor: fbEmailError ? '#dc2626' : undefined }}
                />
                {fbEmailError && (
                  <p style={{ color: '#dc2626', fontSize: 12, margin: '0 0 10px' }}>Please enter your email so we can respond to your ticket.</p>
                )}

                <label className="pw-feedback-label">Your message</label>
                <textarea
                  className="pw-feedback-textarea"
                  placeholder={
                    fbCategory === 'Bug Report'
                      ? 'Describe what happened and the steps to reproduce it…'
                      : fbCategory === 'Feature Request'
                      ? 'Describe the feature and why it would be useful…'
                      : 'Your thoughts, questions or comments…'
                  }
                  value={fbMessage}
                  onChange={(e) => setFbMessage(e.target.value)}
                />

                {fbError && (
                  <p style={{ color: '#b91c1c', fontSize: 13, marginBottom: 8 }}>
                    Something went wrong — please try again.
                  </p>
                )}

                <button
                  className="pw-feedback-submit"
                  onClick={handleSubmitFeedback}
                  disabled={fbSubmitting || !fbMessage.trim()}
                >
                  {fbSubmitting ? 'Sending…' : 'Send Feedback'}
                </button>

                {fbDone && (
                  <div className="pw-feedback-success">
                    ✓ Support Ticket {fbTicketId ? `#${fbTicketId}` : ''} raised — we’ll respond within 48–72 hours. A confirmation has been sent to your email.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Floating Action Button ──────────────────────────────────────── */}
      <button
        className="pw-fab"
        onClick={handleFabClick}
        title="Help & FAQs"
        aria-label="Open help panel"
      >
        {panelOpen ? '✕' : '?'}
      </button>
    </>
  );
};

export default PortalWizard;
